import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaChevronLeft, FaChevronRight, FaCar, FaExchangeAlt, FaCalendarAlt, FaList, FaCalendarWeek, FaClipboardCheck, FaTools, FaPlus, FaCalendarDay } from 'react-icons/fa';
import RentalDetailModal from './RentalDetailModal';
import EditRentalModal from '../RentalModal/EditRentalModal';
import useWindowSize from '../common/useWindowSize';
import { useHandoversForRentals, getHandoverColor } from './HandoverUtils';
import VehicleSelector from './VehicleSelector';
import HandoverCard from './HandoverCard';

const RentalCalendar = ({ rentals, onDateClick, fetchRentalsForDateRange }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(currentDate.getMonth());
  const [currentYear, setCurrentYear] = useState(currentDate.getFullYear());
  const [calendarDays, setCalendarDays] = useState([]);
  const [selectedRental, setSelectedRental] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState('agenda');
  const { width } = useWindowSize();
  const [isNavigating, setIsNavigating] = useState(false);
  const [selectedVehicleId, setSelectedVehicleId] = useState(null);
  
  // Fetch handovers for all rentals
  const { handovers, loading: handoversLoading } = useHandoversForRentals(rentals);

  // Determine view mode based on screen size
  useEffect(() => {
    if (width < 640) {
      // Mobile view defaults to agenda view
      setViewMode('agenda');
    } else {
      // Default to agenda view for all screen sizes per requirement
      setViewMode('agenda');
    }
  }, [width]);
  
useEffect(() => {
  generateCalendarDays();
}, [currentMonth, currentYear, currentDate, rentals, viewMode]);

// Fetch rentals for the current view range when view mode changes
  

// Update the generateCalendarDays function for more reliable operation
const generateCalendarDays = () => {
  const days = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  if (viewMode === 'month') {
    // Full month calendar view
    const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
    const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);
    const daysInMonth = lastDayOfMonth.getDate();
    const firstDayWeekday = firstDayOfMonth.getDay();
    
    // Add padding days from previous month
    const prevMonth = new Date(currentYear, currentMonth, 0);
    const prevMonthDays = prevMonth.getDate();
    for (let i = 0; i < firstDayWeekday; i++) {
      days.push({
        date: new Date(currentYear, currentMonth - 1, prevMonthDays - firstDayWeekday + i + 1),
        isCurrentMonth: false
      });
    }
    
    // Add current month days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        date: new Date(currentYear, currentMonth, i),
        isCurrentMonth: true
      });
    }
    
    // Add padding days from next month if needed
    const remainingDays = (Math.ceil(days.length / 7) * 7) - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        date: new Date(currentYear, currentMonth + 1, i),
        isCurrentMonth: false
      });
    }
  } else if (viewMode === 'week') {
    // Week view - use currentDate as reference point
    const startOfWeek = new Date(currentDate);
    // Adjust to start of week (Sunday)
    startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());
    
    // Generate 7 days from start of week
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      days.push({
        date: new Date(date),
        isCurrentMonth: date.getMonth() === currentMonth
      });
    }
  } else if (viewMode === 'agenda') {
    // Agenda view - next 14 days from currentDate
    for (let i = 0; i < 14; i++) {
      const date = new Date(currentDate);
      date.setDate(currentDate.getDate() + i);
      days.push({
        date: new Date(date),
        isCurrentMonth: date.getMonth() === currentMonth
      });
    }
  } else if (viewMode === 'today') {
    // Today view - single day view for today
    days.push({
      date: new Date(today),
      isCurrentMonth: true
    });
  }
  
  setCalendarDays(days);
};

  
const goToPreviousMonth = () => {
    setIsNavigating(true);
  if (viewMode === 'month') {
    // Create a new date to avoid state update synchronization issues
    const newDate = new Date(currentYear, currentMonth, 1);
    newDate.setMonth(newDate.getMonth() - 1);
    
    // Update both states separately but consistently
    setCurrentMonth(newDate.getMonth());
    setCurrentYear(newDate.getFullYear());
        setTimeout(() => {
    setIsNavigating(false);
  }, 50);
  } else if (viewMode === 'week') {
    // Fix week navigation by using a copy of the current date
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 7);
    
    // Update all states to ensure consistency
    setCurrentDate(newDate);
    setCurrentMonth(newDate.getMonth());
    setCurrentYear(newDate.getFullYear());
        setTimeout(() => {
    setIsNavigating(false);
  }, 50);
  } else if (viewMode === 'agenda') {
    // For agenda view, ensure a full refresh of the 14-day window
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 14);
    
    // Complete state update
    setCurrentDate(newDate);
    setCurrentMonth(newDate.getMonth());
    setCurrentYear(newDate.getFullYear()
  );
      setTimeout(() => {
    setIsNavigating(false);
  }, 50);
  } else if (viewMode === 'today') {
    // For today view, navigate to the previous day
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 1);
    setCurrentDate(newDate);
    setCurrentMonth(newDate.getMonth());
    setCurrentYear(newDate.getFullYear());
    setTimeout(() => {
      setIsNavigating(false);
    }, 50);
  }
};

const goToNextMonth = () => {
    setIsNavigating(true);
  if (viewMode === 'month') {
    // Create a new date to avoid state update synchronization issues
    const newDate = new Date(currentYear, currentMonth, 1);
    newDate.setMonth(newDate.getMonth() + 1);
    
    // Update both states separately but consistently
    setCurrentMonth(newDate.getMonth());
    setCurrentYear(newDate.getFullYear());
        setTimeout(() => {
    setIsNavigating(false);
  }, 50);
  } else if (viewMode === 'week') {
    // Fix week navigation by using a copy of the current date
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 7);
    
    // Update all states to ensure consistency
    setCurrentDate(newDate);
    setCurrentMonth(newDate.getMonth());
    setCurrentYear(newDate.getFullYear());
        setTimeout(() => {
    setIsNavigating(false);
  }, 50);
  } else if (viewMode === 'agenda') {
    // For agenda view, ensure a full refresh of the 14-day window
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 14);
    
    // Complete state update
    setCurrentDate(newDate);
    setCurrentMonth(newDate.getMonth());
    setCurrentYear(newDate.getFullYear());
      setTimeout(() => {
    setIsNavigating(false);
  }, 50);
  } else if (viewMode === 'today') {
    // For today view, navigate to the next day
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 1);
    setCurrentDate(newDate);
    setCurrentMonth(newDate.getMonth());
    setCurrentYear(newDate.getFullYear());
    setTimeout(() => {
      setIsNavigating(false);
    }, 50);
  }
};
  
  const handleRentalClick = (rental) => {
    setSelectedRental(rental);
    setIsModalOpen(true);
  };
  const getRentalsForDay = (day) => {
    // For calendar display purposes, we filter for pickup, return, and handover events
    const filteredRentals = rentals.filter(rental => {
      // Only include pending or confirmed rentals (exclude cancelled and completed)
      if (rental.status !== 'pending' && rental.status !== 'confirmed') {
        return false;
      }
      
      const pickupDate = new Date(rental.pickupDateTime);
      const returnDate = new Date(rental.returnDateTime);
      
      pickupDate.setHours(0, 0, 0, 0);
      returnDate.setHours(0, 0, 0, 0);
      
      const dayDate = new Date(day);
      dayDate.setHours(0, 0, 0, 0);
      
      // Apply vehicle filter if selected
      if (selectedVehicleId && rental.vehicleInstanceId !== selectedVehicleId) {
        return false;
      }
      
      // Only show days with pickup or return
      return dayDate.getTime() === pickupDate.getTime() || 
             dayDate.getTime() === returnDate.getTime();
    });
    
    // Also check for handovers on this day
    if (handovers) {
      // Add an additional property to identify rentals that have handovers on this day
      filteredRentals.forEach(rental => {
        if (handovers[rental._id]) {
          const rentalHandovers = handovers[rental._id];
          rental.handoversForDay = rentalHandovers.filter(handover => {
            const handoverDate = new Date(handover.handoverDate);
            handoverDate.setHours(0, 0, 0, 0);
            
            const dayDate = new Date(day);
            dayDate.setHours(0, 0, 0, 0);
            
            return dayDate.getTime() === handoverDate.getTime();
          });
        }
      });
    }
    
    return filteredRentals;
  };
  
  const isPickupDay = (day, rental) => {
    const pickupDate = new Date(rental.pickupDateTime);
    return day.getDate() === pickupDate.getDate() &&
           day.getMonth() === pickupDate.getMonth() &&
           day.getFullYear() === pickupDate.getFullYear();
  };
  
  const isReturnDay = (day, rental) => {
    const returnDate = new Date(rental.returnDateTime);
    return day.getDate() === returnDate.getDate() &&
           day.getMonth() === returnDate.getMonth() &&
           day.getFullYear() === returnDate.getFullYear();
  };  const getColorForRental = (rental, day) => {
    // Check if this rental has handovers for this day
    if (rental.handoversForDay && rental.handoversForDay.length > 0) {
      const handover = rental.handoversForDay[0]; // Use the first handover for this day
      if (handover.handoverType === 'pickup') {
        return 'bg-teal-500';
      } else if (handover.handoverType === 'return') {
        return 'bg-purple-500';
      }
    }
    
    const pickupDate = new Date(rental.pickupDateTime);
    const returnDate = new Date(rental.returnDateTime);
    
    // Check if this is a pickup or return day for the rental
    const isPickupDay = day && day.getDate() === pickupDate.getDate() &&
                       day.getMonth() === pickupDate.getMonth() && 
                       day.getFullYear() === pickupDate.getFullYear();
    
    const isReturnDay = day && day.getDate() === returnDate.getDate() &&
                       day.getMonth() === returnDate.getMonth() && 
                       day.getFullYear() === returnDate.getFullYear();
    
    if (isPickupDay) {
      // Pickup day - green
      return 'bg-green-500';
    } else if (isReturnDay) {
      // Return day - orange
      return 'bg-orange-500';
    } else {
      // We don't need this anymore since we're not showing active rentals,
      // but keeping it as a fallback
      return 'bg-blue-500';
    }
  };
  
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];  
  // Change removed the toggleViewMode function as we now have direct buttons
  
  return (
    <div className="calendar-container">
      {/* Vehicle Filter */}
      {/* <VehicleSelector
        rentals={rentals}
        selectedVehicleId={selectedVehicleId}
        onSelectVehicle={setSelectedVehicleId}
      /> */}

      {/* Calendar Header */}
      <div className="calendar-controls flex flex-wrap items-center justify-between mb-6">
        <div className="flex items-center">
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={goToPreviousMonth}
            className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
          >
            <FaChevronLeft className="text-gray-700" />
          </motion.button>
          
          <AnimatePresence mode="wait">
            <motion.h3 
              key={`${currentMonth}-${currentYear}-${viewMode}`}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.2 }}
              className="mx-4 text-xl font-bold"
            >
              {viewMode === 'month' 
                ? `${monthNames[currentMonth]} ${currentYear}` 
                : viewMode === 'week'
                ? `Week of ${calendarDays[0]?.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                : `Next 14 Days`}
            </motion.h3>
          </AnimatePresence>
          
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={goToNextMonth}
            className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
          >
            <FaChevronRight className="text-gray-700" />
          </motion.button>
        </div>
          <div className="flex md:flex-row flex-col w-full text-center items-center mt-2 sm:mt-0 pt-4 gap-2">
          <button 
            onClick={() => setViewMode('month')}
            className={`flex w-full items-center px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              viewMode === 'month' 
                ? 'bg-blue-500 text-white' 
                : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
            }`}
          >
            <FaCalendarAlt className="mr-1" />
            Month
          </button>
          
          <button 
            onClick={() => setViewMode('week')}
            className={`flex  w-full items-center px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              viewMode === 'week' 
                ? 'bg-blue-500 text-white' 
                : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
            }`}
          >
            <FaCalendarWeek className="mr-1" />
            Week
          </button>
          
          <button 
            onClick={() => setViewMode('agenda')}
            className={`flex  w-full items-center px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              viewMode === 'agenda' 
                ? 'bg-blue-500 text-white' 
                : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
            }`}
          >
            <FaList className="mr-1" />
            Agenda
          </button>
          
          <button 
            onClick={() => setViewMode('today')}
            className={`flex w-full items-center px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              viewMode === 'today' 
                ? 'bg-blue-500 text-white' 
                : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
            }`}
          >
            <FaCalendarDay className="mr-1" />
            Today
          </button>
        </div>
      </div>

      {isNavigating && (
  <div className="absolute inset-0 bg-white bg-opacity-50 flex items-center justify-center z-10">
    <div className="animate-spin h-6 w-6 border-2 border-blue-500 border-t-transparent rounded-full"></div>
  </div>
)}
      
      {/* Month View */}
      {viewMode === 'month' && (
        <>
          <div className="hidden sm:grid sm:grid-cols-7 gap-1 mb-2">
            {dayNames.map((day, index) => (
              <div key={index} className="text-center text-sm font-medium text-gray-500 py-2">
                {day}
              </div>
            ))}
          </div>
          
          <AnimatePresence>
            <motion.div 
              key={`${currentMonth}-${currentYear}-grid`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
              className="grid grid-cols-1 sm:grid-cols-7 gap-2"
            >
              {calendarDays.map((day, index) => {
                const rentalsForDay = getRentalsForDay(day.date);
                const isToday = day.date.getDate() === new Date().getDate() &&
                               day.date.getMonth() === new Date().getMonth() &&
                               day.date.getFullYear() === new Date().getFullYear();
                
                // For mobile (1-column), only show days with rentals or current month
                const showOnMobile = rentalsForDay.length > 0 || (day.isCurrentMonth && width < 640);
                  return (
                  <motion.div
                    key={`${day.date.toISOString()}-${index}`}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.2, delay: index * 0.01 }}                    className={`relative border rounded-lg cursor-pointer hover:shadow-md transition-all duration-200 group ${
                      showOnMobile ? 'block' : 'hidden sm:block'
                    } ${
                      isToday ? 'bg-blue-50 border-blue-300' : 
                      day.isCurrentMonth ? 'bg-white border-gray-200 hover:bg-blue-50 hover:border-blue-300' : 'bg-gray-50 border-gray-100 text-gray-400 hover:bg-gray-100'
                    }`}
                    onClick={() => onDateClick && onDateClick(day.date)}
                  >                    {/* Day header - more visible on mobile */}
                    <div className={`flex justify-between items-center p-2 ${
                      isToday ? 'bg-blue-100 text-blue-700' : day.isCurrentMonth ? 'bg-gray-50' : 'bg-gray-100'
                    } rounded-t-lg`}>
                      <span className="sm:hidden font-medium">
                        {dayNames[day.date.getDay()]}
                      </span>
                      <span className={`text-sm ${
                        isToday ? 'font-bold text-blue-700' : ''
                      }`}>
                        {day.date.getDate()}
                      </span>
                      {onDateClick && day.isCurrentMonth && (
                        <FaPlus className="w-3 h-3 text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                      )}
                    </div>
                      {/* Rental events - adaptive layout */}
                    <div className="rental-events p-1 space-y-1 max-h-20 sm:max-h-[70px] overflow-y-auto">
                      {rentalsForDay.map((rental, rentalIndex) => {
                        const isPickup = isPickupDay(day.date, rental);
                        const isReturn = isReturnDay(day.date, rental);
                        
                        // Only show pickup and return events, skip active rentals
                        if (!isPickup && !isReturn) return null;
                        
                        return (                          <motion.div 
                            key={`${rental._id}-${rentalIndex}`}
                            initial={{ x: -5, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            transition={{ delay: 0.1 + (rentalIndex * 0.05) }}
                            className={`text-xs rounded px-2 py-1.5 text-white flex items-center justify-between cursor-pointer ${getColorForRental(rental, day.date)}`}
                            whileHover={{ scale: 1.03 }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRentalClick(rental);
                            }}
                          >
                            <div className="flex items-center truncate">
                              {isPickup && <FaCar className="mr-1 flex-shrink-0" />}
                              <span className="truncate">
                                {width < 640 
                                  ? `${rental.customer.lastName} - ${rental.carName}` 
                                  : rental.carName}
                                {rental.vehicleDetails?.licensePlate && width >= 640 && (
                                  <span className="ml-1 text-xs bg-white bg-opacity-25 px-1 rounded-sm">
                                    {rental.vehicleDetails.licensePlate}
                                  </span>
                                )}
                              </span>
                            </div>
                            <div className="flex items-center">
                              {rental.handoversForDay && rental.handoversForDay.length > 0 && (
                                <span className="mr-1 w-2 h-2 bg-white rounded-full animate-pulse"></span>
                              )}
                              {isReturn && <FaExchangeAlt className="ml-1 flex-shrink-0" />}
                            </div>
                          </motion.div>
                        );
                      })}
                      
                      {rentalsForDay.length > 0 && width < 640 && (
                        <div className="text-right text-xs text-gray-500 pr-1">
                          {rentalsForDay.length} rental{rentalsForDay.length !== 1 ? 's' : ''}
                        </div>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          </AnimatePresence>
        </>
      )}
      
      {/* Week View - More detailed and mobile friendly */}
      {viewMode === 'week' && (
        <div className="space-y-2">
          {calendarDays.map((day, index) => {
            const rentalsForDay = getRentalsForDay(day.date);
            const isToday = day.date.getDate() === new Date().getDate() &&
                          day.date.getMonth() === new Date().getMonth() &&
                          day.date.getFullYear() === new Date().getFullYear();
            
            return (              <motion.div
                key={`week-${day.date.toISOString()}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, delay: index * 0.05 }}
                className={`border rounded-lg overflow-hidden ${
                  isToday ? 'bg-blue-50 border-blue-300' : 'bg-white border-gray-200'
                }`}
              >
                <div 
                  className={`p-3 flex justify-between items-center cursor-pointer hover:bg-opacity-80 transition-colors group ${
                    isToday ? 'bg-blue-100' : 'bg-gray-50 hover:bg-blue-50'
                  }`}
                  onClick={() => onDateClick && onDateClick(day.date)}
                >
                  <h3 className="font-medium">
                    {dayNames[day.date.getDay()]}, {day.date.toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric'
                    })}
                    {onDateClick && (
                      <FaPlus className="inline-block ml-2 w-3 h-3 text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                    )}
                  </h3>
                  <span className="text-xs px-2 py-1 rounded-full bg-gray-200 text-gray-700">
                    {rentalsForDay.length} rental{rentalsForDay.length !== 1 ? 's' : ''}
                  </span>
                </div>
                
                {rentalsForDay.length > 0 ? (
                  <div className="p-2 space-y-2">
                    {rentalsForDay.map((rental, rentalIndex) => {
                      const isPickup = isPickupDay(day.date, rental);
                      const isReturn = isReturnDay(day.date, rental);
                      
                      return (
                        <motion.div
                          key={`week-rental-${rental._id}-${rentalIndex}`}
                          initial={{ opacity: 0, x: -5 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.2, delay: 0.1 + (rentalIndex * 0.05) }}                          className={`p-2 rounded flex justify-between items-center cursor-pointer ${
                            getColorForRental(rental, day.date).replace('bg-', 'bg-opacity-90 bg-')
                          }`}
                          whileHover={{ scale: 1.01 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRentalClick(rental);
                          }}
                        >
                          <div className="flex items-center text-white">
                            {isPickup ? (
                              <div className="flex items-center mr-2 px-2 py-0.5 bg-white bg-opacity-30 rounded">
                                <FaCar className="mr-1" /> Pickup
                              </div>
                            ) : isReturn ? (
                              <div className="flex items-center mr-2 px-2 py-0.5 bg-white bg-opacity-30 rounded">
                                <FaExchangeAlt className="mr-1" /> Return
                              </div>
                            ) : (
                              <div className="w-2 h-2 bg-white rounded-full mr-2"></div>
                            )}
                            <div>
                              <div className="font-medium">{rental.carName}</div>
                              <div className="text-xs opacity-90">{rental.customer.firstName} {rental.customer.lastName}</div>
                            </div>
                          </div>
                          <div className="text-xs text-white">
                            {isPickup && new Date(rental.pickupDateTime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                            {isReturn && new Date(rental.returnDateTime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                          </div>
                        </motion.div>
                      );
                    })}
                    
                    {/* Add handover information */}
                    {rentalsForDay.map((rental) => {
                      return (
                        rental.handoversForDay && rental.handoversForDay.length > 0 && (
                          <div className="mt-2 text-xs bg-white bg-opacity-20 rounded-md p-1">
                            {rental.handoversForDay.map((handover, idx) => (
                              <div key={`week-handover-${handover._id}-${idx}`} className="flex items-center">
                                <span className={`px-1.5 py-0.5 rounded-sm ${
                                  handover.handoverType === 'pickup' 
                                    ? 'bg-teal-200 text-teal-800' 
                                    : 'bg-purple-200 text-purple-800'
                                }`}>
                                  {handover.handoverType.charAt(0).toUpperCase() + handover.handoverType.slice(1)} Handover
                                </span>
                                {handover.employee && (
                                  <span className="ml-1 text-gray-800">
                                    with {handover.employee.firstName}
                                  </span>
                                )}
                                {handover.damages && handover.damages.length > 0 && (
                                  <span className="ml-1 bg-red-200 text-red-800 px-1 rounded-sm">
                                    {handover.damages.length} damage(s)
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        )
                      );
                    })}
                  </div>
                ) : (
                  <div className="p-4 text-center text-gray-500 text-sm">
                    No rentals on this day
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      )}

        {/* Agenda View - Mobile optimized list */}
      {viewMode === 'agenda' && (
        <div className="space-y-4">
          {calendarDays.map((day, index) => {
            const rentalsForDay = getRentalsForDay(day.date);
            
            const isToday = day.date.getDate() === new Date().getDate() &&
                          day.date.getMonth() === new Date().getMonth() &&
                          day.date.getFullYear() === new Date().getFullYear();
            
            return (
              <motion.div
                key={`agenda-${day.date.toISOString()}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, delay: index * 0.03 }}
              >
                <div 
                  className={`mb-2 px-3 py-2 rounded-full inline-flex items-center cursor-pointer hover:bg-opacity-80 transition-colors group ${
                    isToday ? 'bg-blue-100 text-blue-800 hover:bg-blue-200' : 'bg-gray-100 text-gray-800 hover:bg-blue-100'
                  }`}
                  onClick={() => onDateClick && onDateClick(day.date)}
                >
                  <FaCalendarAlt className={`mr-2 ${isToday ? 'text-blue-500' : 'text-gray-500'}`} />
                  <h3 className="font-medium">
                    {day.date.toLocaleDateString('en-US', { 
                      weekday: 'short',
                      month: 'short', 
                      day: 'numeric'
                    })}
                  </h3>
                  {onDateClick && (
                    <FaPlus className="ml-2 w-3 h-3 text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                  )}
                </div>
                
                {rentalsForDay.length > 0 ? (
                  <div className="space-y-2 ml-2">
                    {rentalsForDay.map((rental, rentalIndex) => {
                      const isPickup = isPickupDay(day.date, rental);
                      const isReturn = isReturnDay(day.date, rental);
                      
                      return (
                        <motion.div
                          key={`agenda-rental-${rental._id}-${rentalIndex}`}
                          initial={{ opacity: 0, x: -5 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.2, delay: 0.1 + (rentalIndex * 0.05) }}
                          className="border rounded-lg overflow-hidden cursor-pointer"
                          whileHover={{ scale: 1.01 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRentalClick(rental);
                          }}
                        >                        <div className={`p-3 border-l-4 ${
                          isPickup 
                            ? 'border-l-green-500 bg-green-50' 
                            : isReturn 
                            ? 'border-l-orange-500 bg-orange-50'
                            : 'border-l-blue-500 bg-blue-50'
                        }`}>
                          <div className="flex justify-between items-start">
                            <div className="space-y-1">
                              <div className="flex items-center">
                                <h4 className="font-medium text-lg">{rental.carName}</h4>
                                {rental.vehicleDetails?.licensePlate && (
                                  <span className="ml-2 px-2 py-0.5 bg-gray-100 rounded-full text-xs text-gray-600">
                                    {rental.vehicleDetails.licensePlate}
                                  </span>
                                )}
                              </div>
                              <p className="text-gray-600">{rental.customer.firstName} {rental.customer.lastName}</p>
                              
                              <div className="flex items-center space-x-2 text-sm">
                                {isPickup && (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-green-100 text-green-800">
                                    <FaCar className="mr-1" />
                                    Pickup at {new Date(rental.pickupDateTime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                  </span>
                                )}
                                {isReturn && (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-orange-100 text-orange-800">
                                    <FaExchangeAlt className="mr-1" />
                                    Return at {new Date(rental.returnDateTime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                  </span>
                                )}
                              </div>

                              <div className="mt-2 text-sm text-gray-500">
                                {isPickup ? (
                                  <p>Location: {rental.pickupLocation}</p>
                                ) : (
                                  <p>Location: {rental.returnLocation}</p>
                                )}
                              </div>
                            </div>

                            {rental.handoversForDay && rental.handoversForDay.length > 0 && (
                              <div className="bg-gray-50 px-3 py-2 rounded-lg">
                                {rental.handoversForDay.map((handover, idx) => (
                                  <div key={idx} className="text-sm flex items-center space-x-2">
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs ${
                                      handover.handoverType === 'pickup' 
                                        ? 'bg-teal-100 text-teal-800' 
                                        : 'bg-purple-100 text-purple-800'
                                    }`}>
                                      <FaClipboardCheck className="mr-1" />
                                      {handover.handoverType.charAt(0).toUpperCase() + handover.handoverType.slice(1)} Handover
                                    </span>
                                    {handover.employee && (
                                      <span className="text-gray-600">
                                        with {handover.employee.firstName}
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                  </div>
                ) : (
                  <div className="ml-2 text-sm text-gray-500 italic">
                    No rentals scheduled - Click date to create new rental
                  </div>
                )}
              </motion.div>
            );
          })}        </div>
      )}

      {/* Today View */}
      {viewMode === 'today' && (
        <div className="space-y-4">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className="mb-4 px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="text-lg font-semibold text-blue-800 flex items-center">
                <FaCalendarDay className="mr-2" />
                Today's Rentals
              </h3>
              <p className="text-sm text-blue-600">
                {new Date().toLocaleDateString('en-US', { 
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
            </div>

            {(() => {
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const rentalsForToday = getRentalsForDay(today);

              return rentalsForToday.length > 0 ? (
                <div className="space-y-3">
                  {rentalsForToday.map((rental, index) => {
                    const isPickup = isPickupDay(today, rental);
                    const isReturn = isReturnDay(today, rental);
                    
                    return (
                      <motion.div
                        key={`today-rental-${rental._id}`}
                        initial={{ opacity: 0, x: -5 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.2, delay: index * 0.05 }}
                        className="bg-white border rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
                        whileHover={{ scale: 1.01 }}
                        onClick={() => handleRentalClick(rental)}
                      >
                        <div className={`p-4 border-l-4 ${
                          isPickup 
                            ? 'border-l-green-500' 
                            : isReturn 
                            ? 'border-l-orange-500'
                            : 'border-l-blue-500'
                        }`}>
                          <div className="flex justify-between items-start">
                            <div className="space-y-1">
                              <div className="flex items-center">
                                <h4 className="font-medium text-lg">{rental.carName}</h4>
                                {rental.vehicleDetails?.licensePlate && (
                                  <span className="ml-2 px-2 py-0.5 bg-gray-100 rounded-full text-xs text-gray-600">
                                    {rental.vehicleDetails.licensePlate}
                                  </span>
                                )}
                              </div>
                              <p className="text-gray-600">{rental.customer.firstName} {rental.customer.lastName}</p>
                              
                              <div className="flex items-center space-x-2 text-sm">
                                {isPickup && (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-green-100 text-green-800">
                                    <FaCar className="mr-1" />
                                    Pickup at {new Date(rental.pickupDateTime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                  </span>
                                )}
                                {isReturn && (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-orange-100 text-orange-800">
                                    <FaExchangeAlt className="mr-1" />
                                    Return at {new Date(rental.returnDateTime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                  </span>
                                )}
                              </div>

                              <div className="mt-2 text-sm text-gray-500">
                                {isPickup ? (
                                  <p>Location: {rental.pickupLocation}</p>
                                ) : (
                                  <p>Location: {rental.returnLocation}</p>
                                )}
                              </div>
                            </div>

                            {rental.handoversForDay && rental.handoversForDay.length > 0 && (
                              <div className="bg-gray-50 px-3 py-2 rounded-lg">
                                {rental.handoversForDay.map((handover, idx) => (
                                  <div key={idx} className="text-sm flex items-center space-x-2">
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs ${
                                      handover.handoverType === 'pickup' 
                                        ? 'bg-teal-100 text-teal-800' 
                                        : 'bg-purple-100 text-purple-800'
                                    }`}>
                                      <FaClipboardCheck className="mr-1" />
                                      {handover.handoverType.charAt(0).toUpperCase() + handover.handoverType.slice(1)} Handover
                                    </span>
                                    {handover.employee && (
                                      <span className="text-gray-600">
                                        with {handover.employee.firstName}
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
                    <FaCar className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-1">No Rentals Today</h3>
                  <p className="text-gray-500 mb-4">There are no pickups or returns scheduled for today.</p>
                  {onDateClick && (
                    <button
                      onClick={() => onDateClick(new Date())}
                      className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <FaPlus className="mr-2" />
                      Create New Rental
                    </button>
                  )}
                </div>
              );
            })()}
          </motion.div>
        </div>
      )}

      {/* Color Legend */}
      <div className="mt-8 grid grid-cols-2 sm:flex sm:flex-wrap gap-2 sm:gap-4 bg-white p-4 rounded-lg shadow-sm">
        <div className="flex items-center">
          <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
          <span className="text-xs text-gray-600">Pickup</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 rounded-full bg-orange-500 mr-2"></div>
          <span className="text-xs text-gray-600">Return</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 rounded-full bg-teal-500 mr-2"></div>
          <span className="text-xs text-gray-600">Pickup Handover</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 rounded-full bg-purple-500 mr-2"></div>
          <span className="text-xs text-gray-600">Return Handover</span>
        </div>
      </div>
      
      {/* Detail Modal */}
      <EditRentalModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        rental={selectedRental}
      />
    </div>
  );
};

export default RentalCalendar;
